import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { defaultTemplateValues, defaultUsagePlanId } from "./defaults";
import { getFirebaseServices } from "./firebase";
import type {
  AccountAccess,
  AppData,
  AuditLog,
  BusinessAccount,
  BusinessProfile,
  BillingStatus,
  MessageTemplate,
  CsvValidationResult,
  NumberHistoryEntry,
  PhoneLogEntry,
  PhoneLogEvent,
  ProfileAnalytics,
  ReviewPlatform,
  TestingAccount,
  UploadDetails,
  UploadRecord,
  UploadRow,
  UploadRowStatus,
  UsagePlanId,
} from "./types";

// ---- Public API ----

export async function loadAppData(
  userId: string,
  userEmail: string | null,
  profileId?: string,
): Promise<AppData> {
  const account = await ensureBillingAccount(userId, userEmail);
  const profiles = await listProfiles(userId);

  if (!profileId || !profiles.find((p) => p.id === profileId)) {
    return { account, profiles, profile: null, uploads: [], auditLogs: [], analytics: null };
  }

  const profile = profiles.find((p) => p.id === profileId)!;
  const [uploads, auditLogs, analytics] = await Promise.all([
    loadRecentUploads(userId, profileId),
    loadAuditLogs(userId),
    loadProfileAnalytics(userId, profileId),
  ]);

  return { account, profiles, profile, uploads, auditLogs, analytics };
}

export async function listProfiles(accountId: string): Promise<BusinessProfile[]> {
  const { firestore } = requiredServices();
  const snap = await getDocs(
    query(
      collection(firestore, "accounts", accountId, "profiles"),
      orderBy("created_at", "asc"),
    ),
  );
  return snap.docs.map((d) => profileFromDoc(accountId, d.id, d.data()));
}

export async function createProfile(accountId: string, locationName: string): Promise<BusinessProfile> {
  const { firestore } = requiredServices();
  const profileRef = doc(collection(firestore, "accounts", accountId, "profiles"));
  const timestamp = nowIso();
  const profile: BusinessProfile = {
    id: profileRef.id,
    account_id: accountId,
    location_name: locationName,
    business_name: "",
    business_address: "",
    business_phone: "",
    contact_name: "",
    primary_email: null,
    review_platforms: [
      { id: "google", name: "Google", url: "", enabled: true },
      { id: "facebook", name: "Facebook", url: "", enabled: true },
    ],
    is_profile_complete: false,
    created_at: timestamp,
    updated_at: timestamp,
  };
  await setDoc(profileRef, profile);
  await insertAuditLog(accountId, "profile_created", { profile_id: profileRef.id, location_name: locationName });
  return profile;
}

export async function saveBusinessProfile(
  accountId: string,
  profileId: string,
  values: {
    location_name: string;
    business_name: string;
    business_address: string;
    business_phone: string;
    contact_name: string;
    primary_email: string;
    review_platforms: ReviewPlatform[];
  },
): Promise<BusinessProfile> {
  const { firestore } = requiredServices();
  const profileRef = profileDoc(accountId, profileId);
  const existing = await getDoc(profileRef);
  const existingProfile = profileFromDoc(accountId, profileId, existing.data() ?? {});

  const next: BusinessProfile = {
    ...existingProfile,
    ...values,
    is_profile_complete: false,
    updated_at: nowIso(),
  };
  next.is_profile_complete = isBusinessProfileComplete(next);

  await updateDoc(profileRef, {
    ...values,
    is_profile_complete: next.is_profile_complete,
    updated_at: next.updated_at,
  });

  await insertAuditLog(accountId, "profile_updated", { profile_id: profileId });

  const updated = await getDoc(profileRef);
  return profileFromDoc(accountId, profileId, updated.data() ?? {});
}

export async function savePlanSelection(accountId: string, planId: UsagePlanId): Promise<BusinessAccount> {
  const { firestore } = requiredServices();
  const accountRef = accountDoc(accountId);
  await updateDoc(accountRef, { usage_plan_id: planId, updated_at: nowIso() });
  await insertAuditLog(accountId, "plan_selected", { plan_id: planId });
  const updated = await getDoc(doc(firestore, "accounts", accountId));
  return accountFromDoc(accountId, updated.data() ?? {});
}

export async function saveValidatedUpload(
  accountId: string,
  profileId: string,
  userId: string,
  file: File,
  validation: CsvValidationResult,
): Promise<UploadDetails> {
  const { firestore, storage } = requiredServices();
  const storagePath = buildUploadStoragePath(userId, file.name);
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, { contentType: file.type || "text/csv" });

  const uploadRef = doc(collection(firestore, "accounts", accountId, "profiles", profileId, "uploads"));
  const upload: UploadRecord = {
    id: uploadRef.id,
    business_account_id: accountId,
    original_filename: file.name,
    storage_path: storagePath,
    total_rows: validation.summary.totalRows,
    valid_rows: validation.summary.validRows,
    rejected_rows: validation.summary.rejectedRows,
    queued_rows: 0,
    suppressed_rows: 0,
    status: "validated",
    created_at: nowIso(),
    started_at: null,
  };

  await setDoc(uploadRef, upload);
  const savedRows: UploadRow[] = validation.rows.map((row) => ({ ...row, upload_id: upload.id }));
  await writeUploadRows(accountId, profileId, upload.id, savedRows);
  await insertPhoneLogEntries(accountId, profileId, buildUploadLogEntries(savedRows));
  await insertAuditLog(accountId, "upload_validated", {
    profile_id: profileId,
    file_name: file.name,
    storage_path: storagePath,
    total_rows: validation.summary.totalRows,
    valid_rows: validation.summary.validRows,
    rejected_rows: validation.summary.rejectedRows,
  });

  return { upload, rows: savedRows };
}

export async function saveManualBatch(
  accountId: string,
  profileId: string,
  validation: CsvValidationResult,
): Promise<UploadDetails> {
  const { firestore } = requiredServices();

  const uploadRef = doc(collection(firestore, "accounts", accountId, "profiles", profileId, "uploads"));
  const upload: UploadRecord = {
    id: uploadRef.id,
    business_account_id: accountId,
    original_filename: "Manual entry",
    storage_path: null,
    total_rows: validation.summary.totalRows,
    valid_rows: validation.summary.validRows,
    rejected_rows: validation.summary.rejectedRows,
    queued_rows: 0,
    suppressed_rows: 0,
    status: "validated",
    created_at: nowIso(),
    started_at: null,
  };

  await setDoc(uploadRef, upload);
  const savedRows: UploadRow[] = validation.rows.map((row) => ({ ...row, upload_id: upload.id }));
  await writeUploadRows(accountId, profileId, upload.id, savedRows);
  await insertPhoneLogEntries(accountId, profileId, buildUploadLogEntries(savedRows));
  await insertAuditLog(accountId, "manual_batch_created", {
    profile_id: profileId,
    total_rows: validation.summary.totalRows,
    valid_rows: validation.summary.validRows,
    rejected_rows: validation.summary.rejectedRows,
  });

  return { upload, rows: savedRows };
}

export async function addRowsToBatch(
  accountId: string,
  profileId: string,
  uploadId: string,
  validation: CsvValidationResult,
): Promise<UploadDetails> {
  const { firestore } = requiredServices();
  const details = await loadUploadDetails(accountId, profileId, uploadId);
  const maxRowNumber = details.rows.reduce((max, r) => Math.max(max, r.row_number), 0);

  const newRows: UploadRow[] = validation.rows.map((row, i) => ({
    ...row,
    upload_id: uploadId,
    row_number: maxRowNumber + i + 1,
  }));

  await writeUploadRows(accountId, profileId, uploadId, newRows);
  await insertPhoneLogEntries(accountId, profileId, buildUploadLogEntries(newRows));

  const uploadRef = doc(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadId);
  await updateDoc(uploadRef, {
    total_rows: increment(newRows.length),
    valid_rows: increment(validation.summary.validRows),
    rejected_rows: increment(validation.summary.rejectedRows),
  });

  await insertAuditLog(accountId, "batch_rows_added", {
    profile_id: profileId,
    upload_id: uploadId,
    added_rows: newRows.length,
  });

  return loadUploadDetails(accountId, profileId, uploadId);
}

export async function removeRowFromBatch(
  accountId: string,
  profileId: string,
  uploadId: string,
  rowId: string,
): Promise<UploadDetails> {
  const { firestore } = requiredServices();
  const rowRef = doc(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadId, "rows", rowId);
  const rowSnap = await getDoc(rowRef);

  if (!rowSnap.exists()) {
    return loadUploadDetails(accountId, profileId, uploadId);
  }

  const row = uploadRowFromDoc(rowSnap.id, rowSnap.data());
  const isValid = row.status === "valid" || row.status === "queued" || row.status === "optin_sent";

  await deleteDoc(rowRef);

  const uploadRef = doc(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadId);
  if (isValid) {
    await updateDoc(uploadRef, { total_rows: increment(-1), valid_rows: increment(-1) });
  } else {
    await updateDoc(uploadRef, { total_rows: increment(-1), rejected_rows: increment(-1) });
  }

  await insertAuditLog(accountId, "batch_row_removed", {
    profile_id: profileId,
    upload_id: uploadId,
    row_id: rowId,
  });

  return loadUploadDetails(accountId, profileId, uploadId);
}

export async function loadUploadDetails(
  accountId: string,
  profileId: string,
  uploadId: string,
): Promise<UploadDetails> {
  const { firestore } = requiredServices();
  const uploadRef = doc(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadId);
  const uploadSnapshot = await getDoc(uploadRef);

  if (!uploadSnapshot.exists()) {
    throw new Error(`Upload ${uploadId} not found`);
  }

  const upload = uploadFromDoc(accountId, uploadSnapshot.id, uploadSnapshot.data());
  const rowsSnapshot = await getDocs(
    collection(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadId, "rows"),
  );
  const rows = rowsSnapshot.docs.map((d) => uploadRowFromDoc(d.id, d.data()));
  rows.sort((a, b) => a.row_number - b.row_number);

  // Overlay each row with its number's live conversation state (the source of
  // truth). A number can appear in many batches, but only one row "owns" the
  // conversation and receives the mirrored status; every other row freezes at a
  // stale value. Reading the conversation makes every batch show the number's
  // actual current state, matching the activity log (issue #36).
  const convStates = await loadConversationStates(accountId, profileId);
  for (const row of rows) {
    const key = (row.normalized_phone_number ?? "").replace(/\D/g, "");
    const state = key ? convStates.get(key) : undefined;
    if (state) row.live_status = conversationStateToRowStatus(state);
  }

  return { upload, rows };
}

// loadConversationStates returns a map of phone-key (digits only, matching the
// phone_conversations doc id) → conversation state for a profile.
export async function loadConversationStates(
  accountId: string,
  profileId: string,
): Promise<Map<string, string>> {
  const { firestore } = requiredServices();
  const snap = await getDocs(
    collection(firestore, "accounts", accountId, "profiles", profileId, "phone_conversations"),
  );
  const map = new Map<string, string>();
  snap.docs.forEach((d) => {
    const state = String(d.data().state ?? "");
    if (state) map.set(d.id, state);
  });
  return map;
}

// loadNumberHistory aggregates one row per unique number for the Number History
// page (issue #37): loaded time and never-sent status come from the upload rows,
// while current state and sent time come from the authoritative phone_conversation.
export async function loadNumberHistory(
  accountId: string,
  profileId: string,
): Promise<NumberHistoryEntry[]> {
  const { firestore } = requiredServices();
  const digitsKey = (p: string) => (p ?? "").replace(/\D/g, "");

  const map = new Map<string, NumberHistoryEntry>();
  const ensure = (key: string, display: string): NumberHistoryEntry => {
    let e = map.get(key);
    if (!e) {
      e = { phone: key, display, dateLoaded: null, dateSent: null, state: null, rowStatus: null, batchCount: 0 };
      map.set(key, e);
    } else if (!e.display && display) {
      e.display = display;
    }
    return e;
  };

  // Upload rows: earliest loaded time, batch count, and never-sent row status.
  const uploadsSnap = await getDocs(
    collection(firestore, "accounts", accountId, "profiles", profileId, "uploads"),
  );
  await Promise.all(
    uploadsSnap.docs.map(async (uploadDoc) => {
      const uploadCreated = stringValue(uploadDoc.data().created_at);
      const rowsSnap = await getDocs(
        collection(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadDoc.id, "rows"),
      );
      const seen = new Set<string>();
      rowsSnap.docs.forEach((d) => {
        const row = d.data();
        const raw = stringValue(row.normalized_phone_number) || stringValue(row.phone_number);
        const key = digitsKey(raw);
        if (!key) return;
        const e = ensure(key, raw);
        if (uploadCreated && (!e.dateLoaded || uploadCreated < e.dateLoaded)) e.dateLoaded = uploadCreated;
        e.rowStatus = stringValue(row.status) || e.rowStatus;
        if (!seen.has(key)) {
          e.batchCount += 1;
          seen.add(key);
        }
      });
    }),
  );

  // Conversations: authoritative current state + sent time (source of truth).
  const convSnap = await getDocs(
    collection(firestore, "accounts", accountId, "profiles", profileId, "phone_conversations"),
  );
  convSnap.docs.forEach((d) => {
    const data = d.data();
    const phoneField = stringValue(data.phone_number);
    const e = ensure(d.id, phoneField || d.id);
    e.state = stringValue(data.state) || e.state;
    const sent = stringValue(data.last_contacted_at);
    if (sent) e.dateSent = sent;
    if (!e.dateLoaded) e.dateLoaded = stringValue(data.created_at) || null;
  });

  const activityDate = (e: NumberHistoryEntry) => e.dateSent ?? e.dateLoaded ?? "";
  return [...map.values()].sort((a, b) => activityDate(b).localeCompare(activityDate(a)));
}

// conversationStateToRowStatus maps a conversation state onto the upload-row
// status the batch table renders. Mirrors the server's rowStatusForState so the
// SPA and the worker agree on how a state is presented (issue #36).
export function conversationStateToRowStatus(state: string): UploadRowStatus {
  switch (state) {
    case "waiting_opt_in_1":
      return "queued";
    case "waiting_opt_in_2":
      return "optin_1_sent";
    case "waiting_response":
      return "optin_sent";
    case "sending_review_link":
    case "sent_review_link":
      return "completed_yes";
    case "clicked_review":
      return "clicked_review";
    case "sending_feedback_link":
    case "sent_feedback_link":
      return "completed_no";
    case "clicked_form":
      return "clicked_form";
    case "form_submitted":
      return "form_submitted";
    case "opted_out":
      return "opted_out";
    case "send_error":
      return "failed";
    default:
      return "optin_sent";
  }
}

export async function saveBillingSetup(
  accountId: string,
  data: {
    square_customer_id: string;
    square_card_id: string;
    card_last4: string;
    card_brand: string;
  },
): Promise<BusinessAccount> {
  const { firestore } = requiredServices();
  const accountRef = accountDoc(accountId);

  await updateDoc(accountRef, {
    square_customer_id: data.square_customer_id,
    square_card_id: data.square_card_id,
    card_last4: data.card_last4,
    card_brand: data.card_brand,
    billing_status: "pending",
    updated_at: nowIso(),
  });

  await insertAuditLog(accountId, "billing_setup", {
    card_brand: data.card_brand,
    card_last4: data.card_last4,
  });

  const updated = await getDoc(doc(firestore, "accounts", accountId));
  return accountFromDoc(accountId, updated.data() ?? {});
}

export async function saveBillingActivation(
  accountId: string,
  billingStartDate: string,
  nextBillingDate: string,
  tokenBalance: number,
): Promise<BusinessAccount> {
  const { firestore } = requiredServices();
  const accountRef = accountDoc(accountId);

  await updateDoc(accountRef, {
    billing_status: "active",
    billing_start_date: billingStartDate,
    next_billing_date: nextBillingDate,
    token_balance: tokenBalance,
    updated_at: nowIso(),
  });

  await insertAuditLog(accountId, "billing_activated", {
    billing_start_date: billingStartDate,
    next_billing_date: nextBillingDate,
    token_balance: tokenBalance,
  });

  const updated = await getDoc(doc(firestore, "accounts", accountId));
  return accountFromDoc(accountId, updated.data() ?? {});
}

export async function refillTokens(accountId: string, count: number): Promise<BusinessAccount> {
  const { firestore } = requiredServices();
  const accountRef = accountDoc(accountId);
  await updateDoc(accountRef, { token_balance: increment(count), updated_at: nowIso() });
  await insertAuditLog(accountId, "tokens_refilled", { count });
  const updated = await getDoc(doc(firestore, "accounts", accountId));
  return accountFromDoc(accountId, updated.data() ?? {});
}

export async function deductTokens(accountId: string, count: number): Promise<BusinessAccount> {
  const { firestore } = requiredServices();
  const accountRef = accountDoc(accountId);
  const snap = await getDoc(accountRef);
  const current = accountFromDoc(accountId, snap.data() ?? {});
  const newBalance = Math.max(0, current.token_balance - count);
  await updateDoc(accountRef, { token_balance: newBalance, updated_at: nowIso() });
  await insertAuditLog(accountId, "tokens_deducted", { count, new_balance: newBalance });
  const updated = await getDoc(doc(firestore, "accounts", accountId));
  return accountFromDoc(accountId, updated.data() ?? {});
}

export async function clearBatchData(accountId: string, profileId: string): Promise<void> {
  const { firestore } = requiredServices();

  const uploadsSnap = await getDocs(
    collection(firestore, "accounts", accountId, "profiles", profileId, "uploads"),
  );
  for (const uploadDoc of uploadsSnap.docs) {
    const rowsSnap = await getDocs(
      collection(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadDoc.id, "rows"),
    );
    if (rowsSnap.docs.length > 0) {
      const batch = writeBatch(firestore);
      rowsSnap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
    await deleteDoc(uploadDoc.ref);
  }

  for (const subcollection of ["phone_history", "phone_conversations", "phone_log"]) {
    const snap = await getDocs(
      collection(firestore, "accounts", accountId, "profiles", profileId, subcollection),
    );
    if (snap.docs.length > 0) {
      const batch = writeBatch(firestore);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  }

  await insertAuditLog(accountId, "batch_data_cleared", { profile_id: profileId });
}

export async function clearPhoneNumberBlockers(
  accountId: string,
  profileId: string,
  phoneInputs: string[],
): Promise<{ cleared: string[]; invalid: string[] }> {
  const { firestore } = requiredServices();

  const cleared: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const input of phoneInputs) {
    const raw = input.trim();
    if (!raw) continue;
    const normalizedPhone = normalizePhoneNumber(raw);
    if (!normalizedPhone) {
      invalid.push(raw);
      continue;
    }
    if (seen.has(normalizedPhone)) continue;
    seen.add(normalizedPhone);
    cleared.push(normalizedPhone);
  }

  if (cleared.length === 0) {
    throw new Error(
      invalid.length > 0
        ? `No valid 10-digit US phone numbers found (checked: ${invalid.join(", ")}).`
        : "Enter at least one valid 10-digit US phone number.",
    );
  }

  await Promise.all(
    cleared.flatMap((normalizedPhone) => {
      const phoneKey = normalizedPhone.replace(/\D/g, "");
      return [
        deleteDoc(phoneHistoryDoc(accountId, profileId, normalizedPhone)),
        deleteDoc(doc(firestore, "accounts", accountId, "profiles", profileId, "phone_conversations", phoneKey)),
      ];
    }),
  );

  await insertAuditLog(accountId, "phone_number_blockers_cleared", {
    profile_id: profileId,
    phone_numbers: cleared,
    count: cleared.length,
  });

  return { cleared, invalid };
}

export async function deleteAccountData(accountId: string): Promise<void> {
  const { firestore } = requiredServices();

  for (const collName of ["audit_logs", "message_templates"]) {
    const snap = await getDocs(collection(firestore, "accounts", accountId, collName));
    if (snap.docs.length > 0) {
      const batch = writeBatch(firestore);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  }

  // Delete all profiles and their sub-collections
  const profilesSnap = await getDocs(collection(firestore, "accounts", accountId, "profiles"));
  for (const profileDoc of profilesSnap.docs) {
    const profileId = profileDoc.id;
    for (const sub of ["uploads", "phone_history", "phone_conversations"]) {
      const subSnap = await getDocs(
        collection(firestore, "accounts", accountId, "profiles", profileId, sub),
      );
      if (subSnap.docs.length > 0) {
        const batch = writeBatch(firestore);
        if (sub === "uploads") {
          for (const uploadDoc of subSnap.docs) {
            const rowsSnap = await getDocs(
              collection(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadDoc.id, "rows"),
            );
            rowsSnap.docs.forEach((d) => batch.delete(d.ref));
            batch.delete(uploadDoc.ref);
          }
        } else {
          subSnap.docs.forEach((d) => batch.delete(d.ref));
        }
        await batch.commit();
      }
    }
    await deleteDoc(profileDoc.ref);
  }

  await deleteDoc(doc(firestore, "accounts", accountId));
}

export async function markUploadReady(
  accountId: string,
  profileId: string,
  uploadId: string,
): Promise<UploadDetails> {
  const { firestore } = requiredServices();
  const details = await loadUploadDetails(accountId, profileId, uploadId);

  if (details.upload.status !== "validated") {
    return details;
  }

  const now = nowIso();
  const candidates = details.rows.filter((row) => row.status === "valid" && row.normalized_phone_number);
  const histories = new Map<string, Record<string, unknown>>();

  await Promise.all(
    candidates.map(async (row) => {
      const normalized = row.normalized_phone_number!;
      if (histories.has(normalized)) return;
      const historySnapshot = await getDoc(phoneHistoryDoc(accountId, profileId, normalized));
      histories.set(normalized, historySnapshot.data() ?? {});
    }),
  );

  let queuedRows = 0;
  let suppressedRows = 0;
  const rowWrites: Array<Promise<void>> = [];
  const suppressedLogEntries: Omit<PhoneLogEntry, "id">[] = [];

  details.rows.forEach((row) => {
    if (row.status !== "valid" || !row.normalized_phone_number || !row.id) return;

    const history = histories.get(row.normalized_phone_number) ?? {};
    const lastContactedAt = nullableString(history.last_contacted_at);
    const suppressionReason = getSuppressionReason(lastContactedAt, now);
    const rowRef = doc(
      firestore,
      "accounts", accountId, "profiles", profileId, "uploads", uploadId, "rows", row.id,
    );

    if (suppressionReason) {
      suppressedRows += 1;
      rowWrites.push(updateDoc(rowRef, { status: "rejected_suppressed", rejection_reason: suppressionReason }));
      suppressedLogEntries.push({
        timestamp: now,
        phone: row.normalized_phone_number,
        event: "suppressed",
        detail: "Suppressed — contacted within the last year",
      });
      return;
    }

    queuedRows += 1;
    rowWrites.push(updateDoc(rowRef, { status: "queued", rejection_reason: null, queued_at: now }));
    rowWrites.push(setDoc(
      phoneHistoryDoc(accountId, profileId, row.normalized_phone_number),
      {
        phone_number: row.normalized_phone_number,
        last_contacted_at: now,
        last_upload_id: uploadId,
        last_status: "queued",
        total_contact_count: increment(1),
        updated_at: now,
        created_at: nullableString(history.created_at) ?? now,
      },
      { merge: true },
    ));
  });

  const uploadRef = doc(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadId);
  await Promise.all(rowWrites);
  await insertPhoneLogEntries(accountId, profileId, suppressedLogEntries);
  await updateDoc(uploadRef, {
    status: "ready",
    valid_rows: queuedRows,
    rejected_rows: details.upload.total_rows - queuedRows,
    queued_rows: queuedRows,
    suppressed_rows: suppressedRows,
    started_at: now,
  });

  await insertAuditLog(accountId, "upload_marked_ready", {
    profile_id: profileId,
    upload_id: uploadId,
    queued_rows: queuedRows,
    suppressed_rows: suppressedRows,
  });

  return loadUploadDetails(accountId, profileId, uploadId);
}

export async function loadProfileAnalytics(
  accountId: string,
  profileId: string,
): Promise<ProfileAnalytics> {
  const { firestore } = requiredServices();
  const snap = await getDocs(
    collection(firestore, "accounts", accountId, "profiles", profileId, "phone_conversations"),
  );

  const states = snap.docs.map((d) => String(d.data().state ?? ""));

  const inGroup = (...s: string[]) => states.filter((st) => s.includes(st)).length;

  return {
    in_process: inGroup("optin_sent"),
    replied_yes: inGroup("completed_yes", "clicked_review"),
    replied_no: inGroup("completed_no", "clicked_form", "form_submitted"),
    opted_out: inGroup("opted_out"),
    clicked_review: inGroup("clicked_review"),
    clicked_form: inGroup("clicked_form", "form_submitted"),
    submitted_feedback: inGroup("form_submitted"),
    skipped: inGroup("skipped_opted_out", "skipped_recent"),
    total: states.length,
  };
}

export async function insertPhoneLogEntries(
  accountId: string,
  profileId: string,
  entries: Omit<PhoneLogEntry, "id">[],
): Promise<void> {
  if (entries.length === 0) return;
  const { firestore } = requiredServices();
  const chunkSize = 500;
  for (let i = 0; i < entries.length; i += chunkSize) {
    const batch = writeBatch(firestore);
    entries.slice(i, i + chunkSize).forEach((entry) => {
      const ref = doc(collection(firestore, "accounts", accountId, "profiles", profileId, "phone_log"));
      batch.set(ref, entry);
    });
    await batch.commit();
  }
}

export async function loadPhoneLog(
  accountId: string,
  profileId: string,
  count = 500,
): Promise<PhoneLogEntry[]> {
  const { firestore } = requiredServices();
  const snap = await getDocs(
    query(
      collection(firestore, "accounts", accountId, "profiles", profileId, "phone_log"),
      orderBy("timestamp", "desc"),
      limit(count),
    ),
  );
  return snap.docs.map((d) => phoneLogEntryFromDoc(d.id, d.data()));
}

export async function checkIsTestingAccount(email: string): Promise<boolean> {
  try {
    const { firestore } = requiredServices();
    const snap = await getDoc(doc(firestore, "testing_accounts", email));
    return snap.exists();
  } catch {
    return false;
  }
}

export async function loadTestingAccounts(): Promise<TestingAccount[]> {
  const { firestore } = requiredServices();
  const snap = await getDocs(query(
    collection(firestore, "testing_accounts"),
    orderBy("created_at", "asc"),
  ));
  return snap.docs.map((d) => ({
    email: stringValue(d.data().email, d.id),
    added_by: stringValue(d.data().added_by),
    created_at: stringValue(d.data().created_at, nowIso()),
  }));
}

export async function addTestingAccount(email: string, addedByEmail: string): Promise<void> {
  const { firestore } = requiredServices();
  const entry: TestingAccount = {
    email,
    added_by: addedByEmail,
    created_at: nowIso(),
  };
  await setDoc(doc(firestore, "testing_accounts", email), entry);
}

// ---- Account access (shared billing) ----

export async function loadAuthorizedUsers(ownerAccountId: string): Promise<AccountAccess[]> {
  const { firestore } = requiredServices();
  const snap = await getDocs(query(
    collection(firestore, "account_access"),
    where("owner_account_id", "==", ownerAccountId),
  ));
  const results = snap.docs.map((d) => accountAccessFromDoc(d.id, d.data()));
  results.sort((a, b) => a.created_at.localeCompare(b.created_at));
  return results;
}

export async function addAuthorizedUser(
  ownerAccountId: string,
  ownerEmail: string,
  authorizedEmail: string,
  addedByEmail: string,
): Promise<void> {
  const { firestore } = requiredServices();
  const docId = `${ownerAccountId}_${authorizedEmail}`;
  const entry: AccountAccess = {
    id: docId,
    owner_account_id: ownerAccountId,
    owner_email: ownerEmail,
    authorized_email: authorizedEmail,
    added_by: addedByEmail,
    created_at: nowIso(),
  };
  await setDoc(doc(firestore, "account_access", docId), entry);
}

export async function removeAuthorizedUser(ownerAccountId: string, authorizedEmail: string): Promise<void> {
  const { firestore } = requiredServices();
  const docId = `${ownerAccountId}_${authorizedEmail}`;
  await deleteDoc(doc(firestore, "account_access", docId));
}

export async function loadLinkedAccounts(userEmail: string): Promise<AccountAccess[]> {
  try {
    const { firestore } = requiredServices();
    const snap = await getDocs(query(
      collection(firestore, "account_access"),
      where("authorized_email", "==", userEmail),
    ));
    return snap.docs.map((d) => accountAccessFromDoc(d.id, d.data()));
  } catch {
    return [];
  }
}

// ---- Internal helpers ----

function requiredServices() {
  const services = getFirebaseServices();
  if (!services) throw new Error("Firebase not initialized");
  return services;
}

async function ensureBillingAccount(userId: string, userEmail: string | null): Promise<BusinessAccount> {
  const accountRef = accountDoc(userId);
  const existing = await getDoc(accountRef);

  if (existing.exists()) {
    return accountFromDoc(existing.id, existing.data());
  }

  const timestamp = nowIso();
  const account: BusinessAccount = {
    id: userId,
    owner_user_id: userId,
    usage_plan_id: defaultUsagePlanId,
    billing_provider: "square",
    billing_status: "not_configured",
    square_customer_id: null,
    square_card_id: null,
    card_last4: null,
    card_brand: null,
    billing_start_date: null,
    next_billing_date: null,
    token_balance: 0,
    created_at: timestamp,
    updated_at: timestamp,
  };

  await setDoc(accountRef, account);
  await insertAuditLog(account.id, "business_account_created", {});
  return account;
}

async function loadRecentUploads(accountId: string, profileId: string): Promise<UploadRecord[]> {
  const { firestore } = requiredServices();
  const uploadsQuery = query(
    collection(firestore, "accounts", accountId, "profiles", profileId, "uploads"),
    orderBy("created_at", "desc"),
    limit(5),
  );
  const snapshot = await getDocs(uploadsQuery);
  return snapshot.docs.map((d) => uploadFromDoc(accountId, d.id, d.data()));
}

async function loadAuditLogs(accountId: string): Promise<AuditLog[]> {
  const { firestore } = requiredServices();
  const logsQuery = query(
    collection(firestore, "accounts", accountId, "audit_logs"),
    orderBy("created_at", "desc"),
    limit(10),
  );
  const snapshot = await getDocs(logsQuery);
  return snapshot.docs.map((d) => auditLogFromDoc(accountId, d.id, d.data()));
}

async function insertAuditLog(
  accountId: string,
  eventType: string,
  eventPayload: Record<string, unknown>,
) {
  const { firestore, auth } = requiredServices();
  const logRef = doc(collection(firestore, "accounts", accountId, "audit_logs"));
  const log: AuditLog = {
    id: logRef.id,
    business_account_id: accountId,
    actor_user_id: auth.currentUser?.uid ?? null,
    event_type: eventType,
    event_payload: eventPayload,
    created_at: nowIso(),
  };
  await setDoc(logRef, log);
}

async function writeUploadRows(
  accountId: string,
  profileId: string,
  uploadId: string,
  rows: UploadRow[],
) {
  const { firestore } = requiredServices();
  const chunkSize = 500;

  for (let index = 0; index < rows.length; index += chunkSize) {
    const batch = writeBatch(firestore);
    const chunk = rows.slice(index, index + chunkSize);
    chunk.forEach((row) => {
      const rowRef = doc(
        collection(firestore, "accounts", accountId, "profiles", profileId, "uploads", uploadId, "rows"),
      );
      row.id = rowRef.id;
      batch.set(rowRef, row);
    });
    await batch.commit();
  }
}

function accountDoc(accountId: string) {
  const { firestore } = requiredServices();
  return doc(firestore, "accounts", accountId);
}

function profileDoc(accountId: string, profileId: string) {
  const { firestore } = requiredServices();
  return doc(firestore, "accounts", accountId, "profiles", profileId);
}

function phoneHistoryDoc(accountId: string, profileId: string, normalizedPhoneNumber: string) {
  const { firestore } = requiredServices();
  const id = normalizedPhoneNumber.replace(/\D/g, "");
  return doc(firestore, "accounts", accountId, "profiles", profileId, "phone_history", id);
}

function accountFromDoc(id: string, data: Record<string, unknown>): BusinessAccount {
  return {
    id,
    owner_user_id: stringValue(data.owner_user_id, id),
    usage_plan_id: usagePlanIdValue(data.usage_plan_id),
    billing_provider: "square",
    billing_status: billingStatusValue(data.billing_status),
    square_customer_id: nullableString(data.square_customer_id),
    square_card_id: nullableString(data.square_card_id),
    card_last4: nullableString(data.card_last4),
    card_brand: nullableString(data.card_brand),
    billing_start_date: nullableString(data.billing_start_date),
    next_billing_date: nullableString(data.next_billing_date),
    token_balance: nonNegativeNumberValue(data.token_balance, 0),
    created_at: stringValue(data.created_at, nowIso()),
    updated_at: stringValue(data.updated_at, nowIso()),
  };
}

function profileFromDoc(accountId: string, id: string, data: Record<string, unknown>): BusinessProfile {
  let review_platforms = reviewPlatformsValue(data.review_platforms);
  if (review_platforms.length === 0) {
    review_platforms = [
      { id: "google", name: "Google", url: "", enabled: true },
      { id: "facebook", name: "Facebook", url: "", enabled: true },
    ];
  }

  const profile: BusinessProfile = {
    id,
    account_id: stringValue(data.account_id, accountId),
    location_name: stringValue(data.location_name, "Main Location"),
    business_name: stringValue(data.business_name),
    business_address: stringValue(data.business_address),
    business_phone: stringValue(data.business_phone),
    contact_name: stringValue(data.contact_name),
    primary_email: nullableString(data.primary_email),
    review_platforms,
    is_profile_complete: false,
    created_at: stringValue(data.created_at, nowIso()),
    updated_at: stringValue(data.updated_at, nowIso()),
  };

  return { ...profile, is_profile_complete: isBusinessProfileComplete(profile) };
}

function templateFromDoc(accountId: string, data: Record<string, unknown>): MessageTemplate {
  return {
    business_account_id: accountId,
    greeting: stringValue(data.greeting, defaultTemplateValues.greeting),
    satisfaction_question: stringValue(data.satisfaction_question, defaultTemplateValues.satisfactionQuestion),
    unhappy_message: stringValue(data.unhappy_message, defaultTemplateValues.unhappyMessage),
    final_thanks: stringValue(data.final_thanks, defaultTemplateValues.finalThanks),
    created_at: stringValue(data.created_at, nowIso()),
    updated_at: stringValue(data.updated_at, nowIso()),
  };
}

function uploadFromDoc(accountId: string, id: string, data: Record<string, unknown>): UploadRecord {
  return {
    id,
    business_account_id: stringValue(data.business_account_id, accountId),
    original_filename: stringValue(data.original_filename),
    storage_path: nullableString(data.storage_path),
    total_rows: numberValue(data.total_rows),
    valid_rows: numberValue(data.valid_rows),
    rejected_rows: numberValue(data.rejected_rows),
    queued_rows: numberValue(data.queued_rows),
    suppressed_rows: numberValue(data.suppressed_rows),
    status: uploadStatusValue(data.status),
    created_at: stringValue(data.created_at, nowIso()),
    started_at: nullableString(data.started_at),
  };
}

function uploadRowFromDoc(id: string, data: Record<string, unknown>): UploadRow {
  return {
    id,
    upload_id: nullableString(data.upload_id) ?? undefined,
    row_number: numberValue(data.row_number),
    phone_number: stringValue(data.phone_number),
    normalized_phone_number: nullableString(data.normalized_phone_number) ??
      normalizeStoredPhoneNumber(stringValue(data.phone_number)),
    consent_received_at: stringValue(data.consent_received_at),
    consent_method: stringValue(data.consent_method),
    consent_attested_by: stringValue(data.consent_attested_by),
    status: uploadRowStatusValue(data.status),
    rejection_reason: nullableString(data.rejection_reason),
    queued_at: nullableString(data.queued_at),
    sent_at: nullableString(data.sent_at),
  };
}

function phoneLogEntryFromDoc(id: string, data: Record<string, unknown>): PhoneLogEntry {
  return {
    id,
    timestamp: stringValue(data.timestamp, nowIso()),
    phone: stringValue(data.phone),
    event: (stringValue(data.event) || "loaded") as PhoneLogEvent,
    detail: stringValue(data.detail),
  };
}

function accountAccessFromDoc(id: string, data: Record<string, unknown>): AccountAccess {
  return {
    id,
    owner_account_id: stringValue(data.owner_account_id),
    owner_email: stringValue(data.owner_email),
    authorized_email: stringValue(data.authorized_email),
    added_by: stringValue(data.added_by),
    created_at: stringValue(data.created_at, nowIso()),
  };
}

function auditLogFromDoc(accountId: string, id: string, data: Record<string, unknown>): AuditLog {
  return {
    id,
    business_account_id: stringValue(data.business_account_id, accountId),
    actor_user_id: nullableString(data.actor_user_id),
    event_type: stringValue(data.event_type),
    event_payload: objectValue(data.event_payload),
    created_at: stringValue(data.created_at, nowIso()),
  };
}

function buildUploadLogEntries(rows: UploadRow[]): Omit<PhoneLogEntry, "id">[] {
  const ts = new Date().toISOString();
  const rejectionDetails: Record<string, string> = {
    rejected_invalid: "Rejected — invalid phone number",
    rejected_duplicate: "Rejected — already in the system",
    rejected_quota: "Rejected — plan quota exceeded",
    rejected_suppressed: "Rejected — within suppression window",
  };
  return rows.map((row) => {
    const phone = row.normalized_phone_number ?? row.phone_number;
    if (row.status === "valid") {
      return { timestamp: ts, phone, event: "loaded" as PhoneLogEvent, detail: "Loaded into the system" };
    }
    return {
      timestamp: ts,
      phone,
      event: "rejected" as PhoneLogEvent,
      detail: rejectionDetails[row.status] ?? `Rejected (${row.status})`,
    };
  });
}

function buildUploadStoragePath(userId: string, fileName: string) {
  const safeFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
  return `uploads/${userId}/${Date.now()}-${crypto.randomUUID()}-${safeFileName}`;
}

function normalizePhoneNumber(value: string): string | null {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function nowIso() {
  return new Date().toISOString();
}

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function nullableString(value: unknown) {
  return typeof value === "string" ? value : null;
}

function numberValue(value: unknown) {
  return typeof value === "number" ? value : 0;
}

function normalizeStoredPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function nonNegativeNumberValue(value: unknown, fallback: number) {
  return typeof value === "number" && value >= 0 ? value : fallback;
}

function usagePlanIdValue(value: unknown): UsagePlanId {
  return value === "growth_1000" || value === "scale_5000" || value === "starter_250"
    ? value
    : defaultUsagePlanId;
}

function billingStatusValue(value: unknown): BillingStatus {
  return value === "pending" ||
    value === "active" ||
    value === "past_due" ||
    value === "canceled" ||
    value === "not_configured"
    ? value
    : "not_configured";
}

function isBusinessProfileComplete(profile: BusinessProfile) {
  return Boolean(
    profile.business_name.trim() &&
      profile.business_address.trim() &&
      profile.business_phone.trim() &&
      profile.contact_name.trim() &&
      profile.primary_email?.trim() &&
      profile.review_platforms.some((p) => p.enabled && p.url.trim()),
  );
}

function reviewPlatformsValue(value: unknown): ReviewPlatform[] {
  if (Array.isArray(value)) {
    const valid = value.filter((item): item is ReviewPlatform => {
      if (!item || typeof item !== "object") return false;
      const r = item as Record<string, unknown>;
      return typeof r.id === "string" && typeof r.name === "string" &&
        typeof r.url === "string" && typeof r.enabled === "boolean";
    });
    if (valid.length > 0) return valid;
  }
  return [];
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function uploadStatusValue(value: unknown): UploadRecord["status"] {
  return value === "draft" || value === "ready" || value === "validated" ? value : "validated";
}

function uploadRowStatusValue(value: unknown): UploadRow["status"] {
  return value === "valid" ||
    value === "queued" ||
    value === "optin_sent" ||
    value === "sent" ||
    value === "failed" ||
    value === "rejected_invalid" ||
    value === "rejected_duplicate" ||
    value === "rejected_quota" ||
    value === "rejected_suppressed"
    ? value
    : "rejected_invalid";
}

const RESEND_SUPPRESSION_DAYS = 365;

function getSuppressionReason(lastContactedAt: string | null, now: string) {
  if (!lastContactedAt) return null;
  const lastContactedTime = Date.parse(lastContactedAt);
  if (Number.isNaN(lastContactedTime)) return null;
  const elapsedDays = (Date.parse(now) - lastContactedTime) / (24 * 60 * 60 * 1000);
  if (elapsedDays < RESEND_SUPPRESSION_DAYS) {
    return `This number was contacted ${Math.floor(elapsedDays)} days ago and cannot be messaged again for 1 year.`;
  }
  return null;
}
